/**
 * Build-time configuration for the professional console.
 *
 * WHY THIS FILE EXISTS
 *
 * Nine page components each read `process.env.REACT_APP_BASE_URL` directly and interpolated it:
 *
 *   axios.get(`${BASE_URL}/specialties`)
 *
 * Create React App inlines REACT_APP_* at BUILD time and substitutes `undefined` for anything it
 * cannot find. A template literal then produces the STRING "undefined", so the request went to
 *
 *   http://localhost:3000/undefined/specialties
 *
 * — a relative path resolved against the dev server, answered with its own 404. Nothing named the
 * real problem (no .env file), and because the URL looked plausible it read as a backend fault.
 *
 * Reading the variable in one place makes that failure impossible to ship silently:
 *   - a missing value is reported once, by name, saying exactly what to do about it;
 *   - local development falls back to the port the professional backend listens on, so a fresh
 *     clone works without a .env at all;
 *   - `apiUrl()` throws rather than building a URL containing "undefined", so the existing
 *     try/catch + toast in every form surfaces a real message instead of a 404 on a nonsense path.
 *
 * Production is already protected at a second layer: the frontend buildspec fails the CodeBuild
 * run when REACT_APP_BASE_URL is empty (REQUIRED_BUILD_VARS in cicd/buildspec-frontend.yml), so a
 * build missing it never reaches the browser. This file is the defence for local and for any
 * environment built outside that pipeline.
 */

/** Where the professional backend listens locally — matches .env.example and env.example. */
const LOCAL_API_BASE_URL = 'http://localhost:9000/api/v1';

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Resolve the API base, with the trailing slash stripped.
 *
 * Stripping matters because every caller appends a path beginning with `/`; without it a value
 * ending in `/` yields `//specialties`, which some proxies treat as a different route.
 */
function resolveApiBaseUrl() {
  const configured = (process.env.REACT_APP_BASE_URL || '').trim();

  if (configured) {
    return configured.replace(/\/+$/, '');
  }

  if (isDevelopment) {
    // A warning rather than a throw: a fresh clone with no .env should still run. The message
    // names the variable and the file so the fix does not require reading this source.
    console.warn(
      '[config] REACT_APP_BASE_URL is not set. Falling back to ' +
        `${LOCAL_API_BASE_URL}. ` +
        'Copy .env.example to .env and restart the dev server to set it explicitly ' +
        '(CRA reads .env only at startup).',
    );
    return LOCAL_API_BASE_URL;
  }

  // A production-style build with nothing configured. Returning '' keeps module evaluation
  // side-effect free — throwing here would white-screen the whole app before anything renders,
  // which is harder to diagnose than a failing request with a clear message. `apiUrl()` throws
  // instead, at the point of use, where a caller's error handling can show it.
  console.error(
    '[config] REACT_APP_BASE_URL is not set in this build. Every API call will fail. ' +
      'It is supplied by the CodeBuild project (frontend_build_environment_overlay in ' +
      'terraform modules/cicd) and must include the /api/v1 suffix.',
  );
  return '';
}

/**
 * API base URL, e.g. `https://api.tiryaqapp.com/api/v1`. Never has a trailing slash.
 *
 * Prefer `apiUrl()` over interpolating this directly — it is what guarantees a misconfiguration
 * surfaces as an error rather than as a request to a path containing "undefined".
 */
export const API_BASE_URL = resolveApiBaseUrl();

/** True when the API base resolved to something usable. */
export const isApiConfigured = Boolean(API_BASE_URL);

/**
 * Build an absolute API URL from a resource path.
 *
 *   apiUrl('/specialties')            -> https://api.tiryaqapp.com/api/v1/specialties
 *   apiUrl('professionals/42/photo')  -> https://api.tiryaqapp.com/api/v1/professionals/42/photo
 *
 * The leading slash is optional so existing call sites can be migrated verbatim.
 *
 * @throws {Error} when no API base is configured, so the failure is reported instead of becoming
 *   a request to `/undefined/...`.
 */
export function apiUrl(path = '') {
  if (!API_BASE_URL) {
    throw new Error(
      'The application is not configured to reach the server (REACT_APP_BASE_URL is missing). ' +
        'Please contact support.',
    );
  }

  const suffix = String(path);
  if (!suffix) return API_BASE_URL;

  return `${API_BASE_URL}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
}

/**
 * Public URL of this app, used for links shown to a professional after registration.
 *
 * Falls back to the current origin, which is correct in every environment and removes the second
 * variable that could render as "undefined" in a user-visible link.
 */
export const FRONTEND_URL = (
  process.env.REACT_APP_FRONTEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '')
)
  .trim()
  .replace(/\/+$/, '');

/**
 * Stored `registration_source` values (tiryaq-shared PROFESSIONAL_REGISTRATION_SOURCES). Every
 * admin list filters on one, so the main registry and the consultation link never mix.
 */
export const REGISTRATION_SOURCES = {
  MAIN_PLATFORM: 'MAIN_PLATFORM',
  DEDICATED_CONSULTATION_LINK: 'DEDICATED_CONSULTATION_LINK',
};

/**
 * Tiryaq Professional consultation registration link.
 *
 * The e-health app's /consultation-registration page sends doctors straight (no intro slides)
 * to the online / home consultation forms under ROUTE_PREFIX here. Under that prefix the forms
 * post to CREATE_PATH, where api-gateway stamps the registration source server-side (it is never
 * sent from the browser), and the header shows the partner logo. SOURCE is the stored value
 * (tiryaq-shared PROFESSIONAL_REGISTRATION_SOURCES) the /admin-consultation-registered view
 * filters on.
 */
export const CONSULTATION_REGISTRATION = {
  ROUTE_PREFIX: '/consultation-registration',
  TITLE: 'Tiryaq Professional',
  CREATE_PATH: '/professionals/consultation-registration',
  // After submitting, back to the dedicated selection page rather than the main one.
  RETURN_URL: `${FRONTEND_URL}/consultation-registration`,
  SOURCE: REGISTRATION_SOURCES.DEDICATED_CONSULTATION_LINK,
};

/** Whether a pathname belongs to the dedicated consultation registration flow. */
export const isConsultationRegistrationPath = (pathname = '') =>
  pathname === CONSULTATION_REGISTRATION.ROUTE_PREFIX ||
  pathname.startsWith(`${CONSULTATION_REGISTRATION.ROUTE_PREFIX}/`);

/**
 * Introduction slide screens (IntroPage). Must match the <Introduction> routes in App.js.
 * The support contact card is shown on every page except these.
 */
export const INTRO_ROUTES = [
  '/',
  '/doctor-dash',
  '/doctor-home-consultation-dash',
  '/pharmacy-dash',
  '/nurse-form-dash',
  '/physiotherapy-form-dash',
];

/** Whether a pathname is one of the introduction slide screens (trailing slashes ignored). */
export const isIntroPath = (pathname = '') =>
  INTRO_ROUTES.includes(pathname.replace(/\/+$/, '') || '/');

/**
 * Support contact shown by SupportContact.jsx. WHATSAPP_URL is WhatsApp's click-to-chat link,
 * which takes the number in international format as digits only.
 */
const SUPPORT_WHATSAPP_NUMBER = '+964 789 009 9556';

export const SUPPORT_CONTACT = {
  WHATSAPP_NUMBER: SUPPORT_WHATSAPP_NUMBER,
  WHATSAPP_URL: `https://wa.me/${SUPPORT_WHATSAPP_NUMBER.replace(/\D/g, '')}`,
};
